import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Notice, PageHeader, Spinner } from "@/components/common/States";
import { api, errorMessage } from "@/lib/api";
import { LEVELS, TENANT_STATUS } from "@/lib/labels";

const EMPTY = { name: "", npsn: "", level: "SMP", address: "", city: "", province: "", email: "", phone: "", initial_status: "Pending_Verification" };

const F = ({ id, label, hint, ...p }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} {...p} />
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function SchoolCreate() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dups, setDups] = useState(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (confirm = false) => {
    if (f.name.trim().length < 3 || f.city.trim().length < 2 || f.province.trim().length < 2) {
      return setErr("Nama sekolah, kabupaten/kota, dan provinsi wajib diisi.");
    }
    if (f.npsn && !/^\d{8}$/.test(f.npsn)) return setErr("NPSN harus 8 digit angka.");
    setBusy(true);
    setErr("");
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : v]));
      const { data } = await api.post("/platform/schools", { ...body, confirm_duplicate: confirm });
      qc.invalidateQueries();
      toast.success(`Sekolah ${data.name} dibuat dengan ID ${data.id}.`);
      navigate(`/pusat-kendali/sekolah/${data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.candidates) setDups({ ...d });
      else setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Pusat Kendali SIST" title="Buat Sekolah Baru" description="ID sekolah internal dibuat otomatis dan tidak akan berubah." />
      <form className="grid md:grid-cols-2 gap-5" onSubmit={(e) => { e.preventDefault(); setDups(null); submit(false); }} noValidate data-testid="school-create-form">
        <div className="md:col-span-2"><F id="name" label="Nama sekolah *" value={f.name} onChange={set("name")} placeholder="Contoh: SMP Negeri 1 Kota Contoh" data-testid="school-name-input" /></div>
        <F id="npsn" label="NPSN" hint="Opsional, 8 digit. Digunakan untuk deteksi duplikasi." value={f.npsn} onChange={set("npsn")} inputMode="numeric" data-testid="school-npsn-input" />
        <div className="space-y-2">
          <Label>Jenjang *</Label>
          <Select value={f.level} onValueChange={(v) => setF({ ...f, level: v })}>
            <SelectTrigger aria-label="Jenjang" data-testid="school-level-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <F id="city" label="Kabupaten/Kota *" value={f.city} onChange={set("city")} data-testid="school-city-input" />
        <F id="province" label="Provinsi *" value={f.province} onChange={set("province")} data-testid="school-province-input" />
        <div className="md:col-span-2"><F id="address" label="Alamat" value={f.address} onChange={set("address")} data-testid="school-address-input" /></div>
        <F id="email" label="Email sekolah" type="email" value={f.email} onChange={set("email")} data-testid="school-email-input" />
        <F id="phone" label="Telepon" value={f.phone} onChange={set("phone")} data-testid="school-phone-input" />
        <div className="space-y-2">
          <Label>Status awal *</Label>
          <Select value={f.initial_status} onValueChange={(v) => setF({ ...f, initial_status: v })}>
            <SelectTrigger aria-label="Status awal" data-testid="school-initial-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Pending_Verification", "Active"].map((s) => <SelectItem key={s} value={s}>{TENANT_STATUS[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 space-y-4">
          {err && <Notice tone="danger" testId="school-create-error">{err}</Notice>}
          {dups && (
            <Notice tone="warn" testId="school-duplicate-warning">
              <p className="font-semibold">{dups.message}</p>
              <ul className="list-disc pl-5 mt-2">
                {dups.candidates.map((c) => (
                  <li key={c.id}><Link className="underline" to={`/pusat-kendali/sekolah/${c.id}`}>{c.name}</Link> · {c.city} · cocok pada {c.match === "npsn" ? "NPSN" : "nama dan kota"}</li>
                ))}
              </ul>
              {dups.code === "possible_duplicate" && (
                <Button type="button" size="sm" className="mt-3" onClick={() => submit(true)} disabled={busy} data-testid="school-confirm-duplicate-button">Tetap buat sekolah ini</Button>
              )}
            </Notice>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={busy} data-testid="school-create-submit-button">{busy && <Spinner />} Buat sekolah</Button>
            <Button type="button" variant="outline" asChild data-testid="school-create-cancel-button"><Link to="/pusat-kendali/sekolah">Batal</Link></Button>
          </div>
        </div>
      </form>
    </div>
  );
}
